/**
 * RecuperarSenhaPage — solicita reset de senha (B1.5 spec §2.5).
 *
 * URL: /cliente/recuperar-senha
 *
 * Backend: POST /api/v1/cliente/recuperar-senha
 *   - SEMPRE retorna 200 com mesma mensagem (anti-enumeration)
 *   - Se email existe + ativo: gera token UUID + envia email Gmail real
 *   - Se email não existe: silenciosamente não faz nada
 *
 * Frontend NÃO precisa diferenciar — mostra mesma mensagem genérica
 * "se o email existe, você vai receber em alguns minutos".
 *
 * NÃO REVERTER:
 *   - Mesma mensagem em ambos casos (defesa anti-enumeration)
 *   - Form desabilita após submit (anti-spam)
 *   - Sem captcha por agora — TD-B1-RATELIMIT cuida disso
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';

import { Alert, Button, Input, Label } from '@/components/ui';
import { clienteApi } from '@/lib/cliente-api';
import { mensagemErro } from '@/lib/mensagem-publica';

export function RecuperarSenhaPage() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setErrorMsg(null);
    if (!email.trim()) {
      setErrorMsg('Informe o e-mail da sua conta.');
      return;
    }
    setLoading(true);
    try {
      await clienteApi.post('/recuperar-senha', {
        email: email.toLowerCase().trim(),
      });
      setSubmitted(true);
    } catch (err) {
      // Backend SEMPRE retorna 200 — se caiu aqui é rede/servidor. O cliente lê
      // o que fazer, nunca o texto da exceção (era `Erro: ${err.message}`).
      setErrorMsg(
        mensagemErro(err, 'Não conseguimos enviar o link agora. Tente de novo em instantes.'),
      );
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="max-w-md mx-auto pt-8" data-testid="cliente-recuperar-page">
      <div className="bg-paper-surface dark:bg-paper-dark-surface border border-line dark:border-line-dark rounded-lg p-8 shadow-sm">
        <h1 className="mb-2 font-display text-3xl tracking-tight text-ink-1 dark:text-ink-dark-1">
          Esqueceu a senha?
        </h1>
        <p className="mb-6 text-sm text-ink-2 dark:text-ink-dark-2">
          Informe o e-mail da sua conta e a gente manda um link para você criar uma senha nova.
        </p>

        {submitted ? (
          <div data-testid="recuperar-success">
            <Alert variant="success">
              Se este e-mail estiver cadastrado no Portal IGM, o link chega em alguns minutos.
              Vale conferir a caixa de spam.
            </Alert>
            <div className="text-center text-sm pt-4">
              <Link
                to="/cliente/login"
                className="text-red dark:text-red-glow hover:underline"
              >
                ← Voltar ao login
              </Link>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="email" required>
                E-mail
              </Label>
              <Input
                id="email"
                type="email"
                autoComplete="email"
                autoFocus
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                disabled={loading}
                data-testid="recuperar-email-input"
              />
              <p className="mt-1 text-xs text-ink-2 dark:text-ink-dark-2">
                O link vale por 1 hora. Entra com usuário, sem e-mail? Peça ao síndico ou à IGM para definir uma senha nova.
              </p>
            </div>

            {errorMsg && (
              <Alert variant="danger" data-testid="recuperar-error">
                {errorMsg}
              </Alert>
            )}

            <Button
              type="submit"
              variant="primary"
              disabled={loading}
              className="w-full"
              data-testid="recuperar-submit-btn"
            >
              {loading ? 'Enviando…' : 'Enviar link de recuperação'}
            </Button>

            <div className="text-center text-sm pt-2">
              <Link
                to="/cliente/login"
                className="text-red dark:text-red-glow hover:underline"
              >
                ← Voltar ao login
              </Link>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
